/**
 * Assets Redux Selectors
 */

import { createSelector } from '@reduxjs/toolkit'
import type { AssetsState } from './assets.slice'

interface RootState {
  assets: AssetsState
}

function normalizeSearchTerm(value: string): string {
  return value.toLowerCase()
}

function normalizeSearchValue(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase()
  if (typeof value === 'number') return String(value).toLowerCase()
  return ''
}

function matchesSearch(value: unknown, search: string): boolean {
  return normalizeSearchValue(value).includes(search)
}

export const selectAssetsState = (state: RootState) => state.assets
export const selectAssets = (state: RootState) => state.assets.items
export const selectSelectedAsset = (state: RootState) => state.assets.selectedAsset
export const selectAssetStats = (state: RootState) => state.assets.stats
export const selectAssetsFilter = (state: RootState) => state.assets.filter
export const selectAssetsLoading = (state: RootState) => state.assets.loading
export const selectAssetsError = (state: RootState) => state.assets.error
export const selectDriverSubmitting = (state: RootState) => state.assets.driverSubmitting
export const selectDriverSubmitError = (state: RootState) => state.assets.driverSubmitError

export const selectFilteredAssets = createSelector(
  [selectAssets, selectAssetsFilter],
  (assets, filter) => {
    let result = assets

    if (filter.status && filter.status !== 'all') {
      result = result.filter(a => a.status === filter.status)
    }
    if (filter.type && filter.type !== 'all') {
      result = result.filter(a => a.type === filter.type)
    }
    if (filter.ownership && filter.ownership !== 'all') {
      result = result.filter(a => a.ownership === filter.ownership)
    }
    if (filter.search) {
      const search = normalizeSearchTerm(filter.search)
      result = result.filter(a =>
        matchesSearch(a.assetNumber, search) ||
        matchesSearch(a.name.en, search) ||
        matchesSearch(a.plateNumber, search)
      )
    }

    return result
  }
)

export const selectActiveAssets = createSelector(
  [selectAssets],
  assets => assets.filter(a => a.status === 'available')
)

export const selectMaintenanceAssets = createSelector(
  [selectAssets],
  assets => assets.filter(a => a.status === 'maintenance')
)

// ============================================================================
// Resources Selectors
// ============================================================================

export const selectResources = (state: RootState) => state.assets.resources
export const selectSelectedResource = (state: RootState) => state.assets.selectedResource
export const selectResourcesFilter = (state: RootState) => state.assets.resourcesFilter

export const selectFilteredResources = createSelector(
  [selectResources, selectResourcesFilter],
  (resources, filter) => {
    let result = resources

    if (filter.status && filter.status !== 'all') {
      result = result.filter(r => r.status === filter.status)
    }
    if (filter.search) {
      const search = normalizeSearchTerm(filter.search)
      result = result.filter(r =>
        matchesSearch(r.name, search) ||
        matchesSearch(r.description, search) ||
        matchesSearch(r.driver?.name, search) ||
        matchesSearch(r.driver?.nameAr, search) ||
        matchesSearch(r.vehicle?.plateNumber, search) ||
        matchesSearch(r.trailer?.plateNumber, search)
      )
    }

    return result
  }
)

// ============================================================================
// Drivers Selectors
// ============================================================================

export const selectDrivers = (state: RootState) => state.assets.drivers
export const selectSelectedDriver = (state: RootState) => state.assets.selectedDriver
export const selectDriversFilter = (state: RootState) => state.assets.driversFilter

export const selectFilteredDrivers = createSelector(
  [selectDrivers, selectDriversFilter],
  (drivers, filter) => {
    let result = drivers

    if (filter.status && filter.status !== 'all') {
      result = result.filter(d => d.status === filter.status)
    }
    if (filter.licenseType && filter.licenseType !== 'all') {
      result = result.filter(d => d.licenseType === filter.licenseType)
    }
    if (filter.search) {
      const search = normalizeSearchTerm(filter.search)
      result = result.filter(d =>
        matchesSearch(d.name, search) ||
        matchesSearch(d.nameAr, search) ||
        matchesSearch(d.driverId, search) ||
        matchesSearch(d.phone, search) ||
        matchesSearch(d.licenseNumber, search)
      )
    }

    return result
  }
)

// ============================================================================
// Vehicles Selectors
// ============================================================================

export const selectVehicles = (state: RootState) => state.assets.vehicles
export const selectSelectedVehicle = (state: RootState) => state.assets.selectedVehicle
export const selectVehiclesFilter = (state: RootState) => state.assets.vehiclesFilter

export const selectFilteredVehicles = createSelector(
  [selectVehicles, selectVehiclesFilter],
  (vehicles, filter) => {
    let result = vehicles

    if (filter.status && filter.status !== 'all') {
      result = result.filter(v => v.status === filter.status)
    }
    if (filter.type && filter.type !== 'all') {
      result = result.filter(v => v.type === filter.type)
    }
    if (filter.ownership && filter.ownership !== 'all') {
      result = result.filter(v => v.ownership === filter.ownership)
    }
    if (filter.search) {
      const search = normalizeSearchTerm(filter.search)
      result = result.filter(v =>
        matchesSearch(v.plateNumber, search) ||
        matchesSearch(v.type, search) ||
        matchesSearch(v.make, search) ||
        matchesSearch(v.model, search) ||
        matchesSearch(v.metadata?.assetNumber, search)
      )
    }

    return result
  }
)

export const selectAvailableVehicles = createSelector(
  [selectVehicles],
  vehicles => vehicles.filter(v => v.status === 'available')
)

// ============================================================================
// Trailers Selectors
// ============================================================================

export const selectTrailers = (state: RootState) => state.assets.trailers
export const selectSelectedTrailer = (state: RootState) => state.assets.selectedTrailer
export const selectTrailersFilter = (state: RootState) => state.assets.trailersFilter

export const selectFilteredTrailers = createSelector(
  [selectTrailers, selectTrailersFilter],
  (trailers, filter) => {
    let result = trailers

    if (filter.status && filter.status !== 'all') {
      result = result.filter(t => t.status === filter.status)
    }
    if (filter.type && filter.type !== 'all') {
      result = result.filter(t => (t as any).type === filter.type)
    }
    if (filter.search) {
      const search = normalizeSearchTerm(filter.search)
      result = result.filter(t =>
        matchesSearch(t.plateNumber, search) ||
        matchesSearch((t as any).type, search) ||
        matchesSearch(t.metadata?.make, search) ||
        matchesSearch(t.metadata?.model, search)
      )
    }

    return result
  }
)

export const selectAvailableTrailers = createSelector(
  [selectTrailers],
  trailers => trailers.filter(t => t.status === 'available')
)

// ============================================================================
// Pagination Selectors
// ============================================================================

export const selectDriversPagination = (state: RootState) => state.assets.driversPagination
export const selectVehiclesPagination = (state: RootState) => state.assets.vehiclesPagination
export const selectTrailersPagination = (state: RootState) => state.assets.trailersPagination
export const selectResourcesPagination = (state: RootState) => state.assets.resourcesPagination

// ============================================================================
// Active Filter Count Selectors
// ============================================================================

/**
 * Helper to count filter values (handles arrays, single values, and 'all')
 */
function countFilterValue(value: unknown): number {
  if (!value || value === 'all') return 0
  if (Array.isArray(value)) return value.length
  return 1
}

/**
 * Count active filters for drivers (excluding 'all' values and search)
 */
export const selectDriversActiveFilterCount = createSelector(
  [selectDriversFilter],
  (filter) => {
    let count = 0
    count += countFilterValue(filter.status)
    count += countFilterValue(filter.licenseType)
    count += filter.name ? 1 : 0
    count += filter.phone ? 1 : 0
    count += filter.employeeNumber ? 1 : 0
    count += filter.isBinded !== 'all' && filter.isBinded !== undefined ? 1 : 0
    return count
  }
)

/**
 * Count active filters for vehicles (excluding 'all' values and search)
 */
export const selectVehiclesActiveFilterCount = createSelector(
  [selectVehiclesFilter],
  (filter) => {
    let count = 0
    count += countFilterValue(filter.status)
    count += countFilterValue(filter.type)
    count += countFilterValue(filter.ownership)
    count += filter.licensePlate ? 1 : 0
    count += filter.isBinded !== 'all' && filter.isBinded !== undefined ? 1 : 0
    return count
  }
)

/**
 * Count active filters for resources (excluding 'all' values and search)
 */
export const selectResourcesActiveFilterCount = createSelector(
  [selectResourcesFilter],
  (filter) => {
    let count = 0
    count += countFilterValue(filter.status)
    count += filter.driverName ? 1 : 0
    count += filter.vehicleLicensePlate ? 1 : 0
    count += filter.driverPhone ? 1 : 0
    return count
  }
)

export const selectDeactivationWarning = (state: RootState) => state.assets.deactivationWarning
